import React, { FC, useEffect, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { TextInput } from 'shared/components/Form/TextInput';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { WithdrawRequestFields } from 'modules/payments/hooks/use-withdraw-request-form';
import { SelectInput } from 'shared/components/Form/SelectInput';
import { getCountryList } from 'modules/utils';

interface ComponentProps {
  form: UseFormReturn<WithdrawRequestFields>;
  isDisabled: boolean;
}

export const WithdrawRequestForm: FC<ComponentProps> = ({ form, isDisabled }) => {
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
  } = form;

  const countries = useMemo(getCountryList, []);

  const emailErrors = useMemo(() => {
    if (errors.destinationEmail?.message && errors.isEmailConfirmed?.message) {
      return [errors.destinationEmail.message];
    }

    return [errors.destinationEmail?.message, errors.isEmailConfirmed?.message].filter(Boolean);
  }, [errors.destinationEmail?.message, errors.isEmailConfirmed?.message]);

  useEffect(() => {
    trigger('country'); // see paypalAllowedWithdrawRequestCountryCodes validation
  }, [trigger]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Typography mb={1}>PayPal email address:</Typography>

          <Controller
            control={control}
            name="destinationEmail"
            render={({ field: { onChange, value, ...rest } }) => (
              <TextInput
                placeholder="E.g. email-address@example.com"
                onChange={(e) => {
                  setValue('isEmailConfirmed', false, { shouldValidate: true });
                  onChange(e);
                }}
                value={value}
                disabled={isDisabled}
                {...rest}
              />
            )}
          />

          <Box ml={2} mt={1}>
            <Controller
              control={control}
              name="isEmailConfirmed"
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={<Switch checked={value} onChange={onChange} />}
                  label={<Typography>I confirm, this is my email address</Typography>}
                />
              )}
            />
          </Box>
        </Box>
      </Box>

      {emailErrors.length ? (
        <Box mb={1}>
          <ErrorMessage error={emailErrors.join('. ')} withSidePadding={false} />
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <Typography mb={1}>Country:</Typography>

          <Controller
            control={control}
            name="country"
            render={({ field: { onChange, value } }) => (
              <SelectInput
                onChange={onChange}
                options={countries}
                value={value}
                disabled={isDisabled}
              />
            )}
          />
        </Box>
      </Box>

      {errors.country?.message && (
        <Box>
          <ErrorMessage error={errors.country.message} withSidePadding={false} />
        </Box>
      )}
    </Box>
  );
};
